import React, { useRef, useState, useCallback } from 'react';
import { User, Mail, Building2, Calendar, Phone, Globe, Upload, Trash2, Loader2, CalendarDays, Clock, Check, Tag, BarChart3, Radio } from 'lucide-react';
import { SettingsSection, SettingsToggle } from '../index';
import { updateSettings } from '../../../services/settingsApi';
import { AD_PLATFORMS } from '../../../constants/settings';

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

const DEFAULT_AVAILABILITY = {
  monday: { enabled: true, start: '09:00', end: '18:00' },
  tuesday: { enabled: true, start: '09:00', end: '18:00' },
  wednesday: { enabled: true, start: '09:00', end: '18:00' },
  thursday: { enabled: true, start: '09:00', end: '18:00' },
  friday: { enabled: true, start: '09:00', end: '18:00' },
  saturday: { enabled: true, start: '09:00', end: '13:00' },
  sunday: { enabled: false, start: '09:00', end: '13:00' },
};

const TabCompany = ({
  user,
  settings,
  onChange,
  onLogoUpload,
  onLogoDelete,
  uploadingLogo,
}) => {
  const logoInputRef = useRef(null);
  const [availability, setAvailability] = useState(
    settings?.marketplace_availability || DEFAULT_AVAILABILITY
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateSetting = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-BE', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const saveAvailability = useCallback(async (newAvailability) => {
    setSaving(true);
    setSaved(false);
    try {
      await updateSettings({ marketplace_availability: newAvailability });
      onChange({ ...settings, marketplace_availability: newAvailability });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Error saving availability:', err);
    } finally {
      setSaving(false);
    }
  }, [settings, onChange]);

  const updateDay = (dayKey, field, value) => {
    const updated = {
      ...availability,
      [dayKey]: { ...availability[dayKey], [field]: value }
    };
    setAvailability(updated);
    clearTimeout(window._availabilitySaveTimeout);
    window._availabilitySaveTimeout = setTimeout(() => {
      saveAvailability(updated);
    }, 800);
  };

  const toggleDay = (dayKey) => {
    const updated = {
      ...availability,
      [dayKey]: { ...availability[dayKey], enabled: !availability[dayKey]?.enabled }
    };
    setAvailability(updated);
    saveAvailability(updated);
  };

  return (
    <div className="space-y-6">
      {/* Profile (read-only) */}
      <SettingsSection title="Profil" icon={User}>
        <div className="space-y-4">
          <div className="flex items-center">
            <User className="h-5 w-5 text-secondary mr-3" />
            <div>
              <p className="text-sm text-muted">Nom</p>
              <p className="font-medium text-primary">{user?.name}</p>
            </div>
          </div>

          <div className="flex items-center">
            <Mail className="h-5 w-5 text-secondary mr-3" />
            <div>
              <p className="text-sm text-muted">Email</p>
              <p className="font-medium text-primary">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-secondary mr-3" />
            <div>
              <p className="text-sm text-muted">Membre depuis</p>
              <p className="font-medium text-primary">{formatDate(user?.created_at)}</p>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* Company Information */}
      <SettingsSection title="Informations entreprise" icon={Building2}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Nom commercial
            </label>
            <input
              type="text"
              value={settings?.company_name || ''}
              onChange={(e) => updateSetting('company_name', e.target.value)}
              placeholder="Ex: Auto Excellence SPRL"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <Phone className="h-4 w-4 inline mr-1" />
              Téléphone
            </label>
            <input
              type="tel"
              value={settings?.company_phone || ''}
              onChange={(e) => updateSetting('company_phone', e.target.value)}
              placeholder="Ex: +32 2 123 45 67"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              <Globe className="h-4 w-4 inline mr-1" />
              Site web
            </label>
            <input
              type="url"
              value={settings?.company_website || ''}
              onChange={(e) => updateSetting('company_website', e.target.value)}
              placeholder="Ex: https://www.auto-excellence.be"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Code postal
            </label>
            <input
              type="text"
              value={settings?.company_postal_code || ''}
              onChange={(e) => updateSetting('company_postal_code', e.target.value)}
              placeholder="Ex: 1410"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
            />
            <p className="mt-1 text-xs text-muted">Utilisé pour pré-remplir le code postal sur 2ememain.be et AutoScout24.be</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Ville
            </label>
            <input
              type="text"
              value={settings?.company_city || ''}
              onChange={(e) => updateSetting('company_city', e.target.value)}
              placeholder="Ex: Waterloo"
              className="w-full bg-dark-input border border-dark-border text-primary placeholder-muted rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-carmin"
            />
            <p className="mt-1 text-xs text-muted">Utilisé pour pré-remplir la ville sur AutoScout24.be</p>
          </div>
        </div>
      </SettingsSection>

      {/* Logo */}
      <SettingsSection title="Logo" icon={Building2}>
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Votre logo sera utilisé pour le watermark sur les photos et affiché dans les stories.
          </p>

          {settings?.logo_url ? (
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-dark-border rounded-lg overflow-hidden bg-dark-hover">
                <img
                  src={settings.logo_url}
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <button
                onClick={onLogoDelete}
                className="inline-flex items-center px-3 py-2 border border-red-500/30 text-red-400 rounded-md hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </button>
            </div>
          ) : (
            <div>
              <input
                type="file"
                ref={logoInputRef}
                onChange={onLogoUpload}
                accept="image/png,image/jpeg"
                className="hidden"
              />
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="inline-flex items-center px-4 py-2 border-2 border-dashed border-dark-border rounded-md text-secondary hover:border-carmin hover:text-carmin disabled:opacity-50"
              >
                {uploadingLogo ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5 mr-2" />
                )}
                Uploader un logo
              </button>
              <p className="mt-2 text-sm text-muted">PNG ou JPEG, max 5MB</p>
            </div>
          )}
        </div>
      </SettingsSection>

      {/* Availability */}
      <SettingsSection title="Disponibilités pour les visites" icon={CalendarDays}>
        <p className="text-sm text-muted mb-4">
          Configurez vos créneaux de disponibilité pour que les autres professionnels puissent vous demander un rendez-vous de visite via WhatsApp.
        </p>

        <div className="space-y-3">
          {DAYS.map(({ key, label }) => {
            const day = availability[key] || { enabled: false, start: '09:00', end: '18:00' };
            return (
              <div key={key} className={`flex items-center gap-4 p-3 rounded-lg border ${
                day.enabled ? 'border-carmin/30 bg-carmin/10' : 'border-dark-border bg-dark-hover'
              }`}>
                <button
                  onClick={() => toggleDay(key)}
                  className={`w-10 h-6 rounded-full relative transition-colors ${
                    day.enabled ? 'bg-carmin' : 'bg-dark-border'
                  }`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    day.enabled ? 'left-[18px]' : 'left-0.5'
                  }`} />
                </button>

                <span className={`w-24 text-sm font-medium ${day.enabled ? 'text-primary' : 'text-muted'}`}>
                  {label}
                </span>

                {day.enabled && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-secondary" />
                    <input
                      type="time"
                      value={day.start}
                      onChange={(e) => updateDay(key, 'start', e.target.value)}
                      className="bg-dark-input border border-dark-border text-primary rounded px-2 py-1 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
                    />
                    <span className="text-secondary">à</span>
                    <input
                      type="time"
                      value={day.end}
                      onChange={(e) => updateDay(key, 'end', e.target.value)}
                      className="bg-dark-input border border-dark-border text-primary rounded px-2 py-1 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
                    />
                  </div>
                )}
                {!day.enabled && (
                  <span className="text-sm text-muted">Fermé</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm">
          {saving && (
            <span className="text-muted flex items-center gap-1">
              <Loader2 className="h-4 w-4 animate-spin" /> Enregistrement...
            </span>
          )}
          {saved && (
            <span className="text-green-400 flex items-center gap-1">
              <Check className="h-4 w-4" /> Enregistré
            </span>
          )}
        </div>
      </SettingsSection>

      {/* Offer expiration */}
      <SettingsSection title="Paramètres des offres" icon={Tag}>
        <p className="text-sm text-muted mb-4">
          Définissez le délai d'expiration des offres que vous recevez. Passé ce délai, l'offre sera automatiquement expirée.
        </p>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-label">Expiration des offres :</label>
          <select
            value={settings?.offer_expiration_hours || 48}
            onChange={async (e) => {
              const hours = parseInt(e.target.value);
              try {
                await updateSettings({ offer_expiration_hours: hours });
                onChange({ ...settings, offer_expiration_hours: hours });
              } catch (err) {
                console.error('Error saving offer expiration:', err);
              }
            }}
            className="bg-dark-input border border-dark-border text-primary rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
          >
            <option value={1}>1 heure</option>
            <option value={2}>2 heures</option>
            <option value={4}>4 heures</option>
            <option value={8}>8 heures</option>
            <option value={12}>12 heures</option>
            <option value={24}>24 heures</option>
            <option value={48}>48 heures</option>
            <option value={72}>72 heures (3 jours)</option>
            <option value={168}>1 semaine</option>
            <option value={336}>2 semaines</option>
            <option value={720}>30 jours</option>
          </select>
        </div>
      </SettingsSection>

      {/* Stock Insights Thresholds */}
      <SettingsSection title="Alertes stock" icon={BarChart3}>
        <p className="text-sm text-muted mb-4">
          Personnalisez les seuils d'alerte pour votre gestion de stock. Ces seuils déterminent quand un véhicule est signalé dans vos insights.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Stock dormant (jours)
            </label>
            <p className="text-xs text-muted mb-2">
              Un véhicule disponible depuis plus de ce nombre de jours sera signalé comme dormant.
            </p>
            <select
              value={settings?.stock_alert_dormant_days || 30}
              onChange={async (e) => {
                const days = parseInt(e.target.value);
                try {
                  await updateSettings({ stock_alert_dormant_days: days });
                  onChange({ ...settings, stock_alert_dormant_days: days });
                } catch (err) {
                  console.error('Error saving threshold:', err);
                }
              }}
              className="bg-dark-input border border-dark-border text-primary rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
            >
              <option value={7}>7 jours</option>
              <option value={14}>14 jours</option>
              <option value={21}>21 jours</option>
              <option value={30}>30 jours (recommandé)</option>
              <option value={45}>45 jours</option>
              <option value={60}>60 jours</option>
              <option value={90}>90 jours</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Préparation lente (jours)
            </label>
            <p className="text-xs text-muted mb-2">
              Un véhicule en préparation depuis plus de ce nombre de jours sera signalé.
            </p>
            <select
              value={settings?.stock_alert_preparation_days || 7}
              onChange={async (e) => {
                const days = parseInt(e.target.value);
                try {
                  await updateSettings({ stock_alert_preparation_days: days });
                  onChange({ ...settings, stock_alert_preparation_days: days });
                } catch (err) {
                  console.error('Error saving threshold:', err);
                }
              }}
              className="bg-dark-input border border-dark-border text-primary rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
            >
              <option value={3}>3 jours</option>
              <option value={5}>5 jours</option>
              <option value={7}>7 jours (recommandé)</option>
              <option value={10}>10 jours</option>
              <option value={14}>14 jours</option>
              <option value={21}>21 jours</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-label mb-2">
              Aucun intérêt (jours)
            </label>
            <p className="text-xs text-muted mb-2">
              Un véhicule en ligne depuis ce nombre de jours sans aucun clic sera signalé. Nécessite la vitrine activée.
            </p>
            <select
              value={settings?.stock_alert_no_interest_days || 14}
              onChange={async (e) => {
                const days = parseInt(e.target.value);
                try {
                  await updateSettings({ stock_alert_no_interest_days: days });
                  onChange({ ...settings, stock_alert_no_interest_days: days });
                } catch (err) {
                  console.error('Error saving threshold:', err);
                }
              }}
              className="bg-dark-input border border-dark-border text-primary rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-carmin focus:border-carmin"
            >
              <option value={7}>7 jours</option>
              <option value={14}>14 jours (recommandé)</option>
              <option value={21}>21 jours</option>
              <option value={30}>30 jours</option>
              <option value={45}>45 jours</option>
            </select>
          </div>
        </div>
      </SettingsSection>

      {/* Ad Platforms */}
      <SettingsSection title="Plateformes d'annonces" icon={Radio}>
        <p className="text-sm text-muted mb-4">
          Activez les plateformes que vous utilisez. Seules les plateformes actives
          sont prises en compte dans votre score de visibilité.
        </p>
        <div className="space-y-3">
          {AD_PLATFORMS.map(p => (
            <SettingsToggle
              key={p.id}
              label={p.name}
              checked={settings?.active_ad_platforms?.[p.id] !== false}
              onChange={async (val) => {
                const updated = { ...(settings?.active_ad_platforms || {}), [p.id]: val };
                try {
                  await updateSettings({ active_ad_platforms: updated });
                  onChange({ ...settings, active_ad_platforms: updated });
                } catch (err) {
                  console.error('Error saving ad platform toggle:', err);
                }
              }}
            />
          ))}
        </div>
      </SettingsSection>

      {/* Subscription */}
      <SettingsSection title="Abonnement">
        <div className="border border-dark-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-primary">
                Plan {user?.plan === 'free' ? 'Gratuit' : 'Pro'}
              </h3>
              <p className="text-sm text-muted">
                {user?.plan === 'free' ? 'Fonctionnalités de base' : 'Toutes les fonctionnalités'}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              user?.plan === 'free' ? 'bg-dark-hover text-label' : 'bg-carmin/10 text-carmin-light'
            }`}>
              {user?.plan === 'free' ? 'Gratuit' : 'Pro'}
            </span>
          </div>
        </div>
        {user?.plan === 'free' && (
          <div className="mt-4 p-4 bg-carmin/10 border border-carmin/30 rounded-lg">
            <p className="text-sm text-carmin-light">
              Passez au plan Pro pour débloquer toutes les fonctionnalités.
            </p>
            <button className="mt-3 px-4 py-2 bg-carmin text-white rounded-md hover:bg-carmin-light text-sm">
              Passer au Pro
            </button>
          </div>
        )}
      </SettingsSection>
    </div>
  );
};

export default TabCompany;
